// Determine API URL based on environment
// Production: uses REACT_APP_API_URL from .env.production or detects from hostname
// Development: uses REACT_APP_API_URL from .env (localhost)
const getApiBaseUrl = () => {
  // If environment variable is set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Auto-detect based on hostname
  const hostname = window.location.hostname;
  
  // Production domains
  if (hostname === 'digihr.in' || hostname === 'www.digihr.in') {
    return 'https://digihr.in/api';
  }
  
  // Local development
  return 'http://127.0.0.1:5015/api';
};

const API_BASE_URL = getApiBaseUrl();

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Get auth token from localStorage
  getAuthToken() {
    return localStorage.getItem('token') || localStorage.getItem('ct_token');
  }

  // Set auth token
  setAuthToken(token) {
    localStorage.setItem('token', token);
    localStorage.setItem('ct_token', token); // Keep both for compatibility
  }

  // Remove auth token
  removeAuthToken() {
    localStorage.removeItem('token');
    localStorage.removeItem('ct_token');
  }

  // Default headers
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    const token = this.getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const hadToken = !!this.getAuthToken();
    const config = {
      headers: this.getHeaders(),
      ...options,
    };

    try {
      let response;
      try {
        response = await fetch(url, config);
      } catch {
        // Network failure (offline, DNS, server down) - fetch only throws a bare "Failed to fetch"
        throw new Error("Can't reach DigiHR right now. Please check your internet connection and try again.");
      }
      let data;
      try {
        data = await response.json();
      } catch {
        // Non-JSON body, e.g. an HTML 502/504 page from the proxy during an outage
        throw new Error(response.ok
          ? 'Unexpected response from the server. Please try again.'
          : 'DigiHR is temporarily unavailable. Please try again in a few minutes.');
      }

      if (!response.ok) {
        // Session expiration only applies to calls that were actually
        // authenticated - a 401 on a public call (e.g. login with bad
        // credentials) must surface as a normal error, not a redirect.
        if (response.status === 401 && hadToken) {
          this.removeAuthToken();
          window.location.href = '/login';
          return;
        }
        throw new Error(data.message || 'An error occurred');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // GET request
  async get(endpoint, params = {}) {
    // Filter out undefined, null, and empty string values
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => 
        value !== undefined && value !== null && value !== ''
      )
    );
    const queryString = new URLSearchParams(filteredParams).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url);
  }

  // POST request
  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // POST request with FormData (for file uploads)
  async postFormData(endpoint, formData) {
    const url = `${this.baseURL}${endpoint}`;
    const token = this.getAuthToken();
    
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    // Don't set Content-Type for FormData - browser sets it automatically with boundary

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });
      
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          this.removeAuthToken();
          window.location.href = '/login';
          return;
        }
        throw new Error(data.message || 'An error occurred');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // PUT request
  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // PUT request with FormData (for file uploads)
  async putFormData(endpoint, formData) {
    const url = `${this.baseURL}${endpoint}`;
    const token = this.getAuthToken();
    
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    // Don't set Content-Type for FormData - browser sets it automatically with boundary

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: formData,
      });
      
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          this.removeAuthToken();
          window.location.href = '/login';
          return;
        }
        throw new Error(data.message || 'An error occurred');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // PATCH request
  async patch(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // DELETE request
  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  }

  // File download
  async downloadFile(endpoint, filename) {
    const url = `${this.baseURL}${endpoint}`;
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${this.getAuthToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to download file');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  }
}

// Create singleton instance
const apiService = new ApiService();

export default apiService;